import { SettingKey } from 'data/ppoints/constants';
import { useFirstSelectedProvider } from 'features/settings/components/ppoints/dashboard/usage-progress-card/hooks/useFirstSelectedProvider';
import useCurrentSubscriptionHelper from 'hooks/billing/useCurrentSubscriptionHelper';
import useSettings from 'hooks/useSettings';
import { ProviderToCountrySetting } from 'models/ppoints/settings/SettingsToEcom';
import { useMemo } from 'react';
import { Feature } from '../../../pb/subscription/v1/addon_pb';

export interface ProviderLimitExceeded {
    /** True when more carriers are active than the current plan allows */
    isLimitExceeded: boolean;
    /** Carrier limit of the current plan, `undefined` while loading or when the plan has no such limit */
    limit: number | undefined;
    /** Display name of the current plan */
    planName: string | undefined;
    /** Number of distinct active carriers */
    activeCount: number;
    isLoading: boolean;
}

/**
 * Compares the carrier limit of the current (non-cancelled) plan with the carriers active in
 * `provider_to_country`. Exceeding it means the merchant moved to a plan with a lower limit than they
 * were using, so the app has to stay deactivated until they pick which carriers to keep.
 */
const useProviderLimitExceeded = (): ProviderLimitExceeded => {
    const {
        currentSubscription,
        addons,
        isCancelled,
        isLoading: isSubscriptionLoading,
    } = useCurrentSubscriptionHelper();
    const { fetching: isSettingsLoading } = useSettings<ProviderToCountrySetting>(SettingKey.provider_to_country);
    const { activeCount } = useFirstSelectedProvider();

    const limit = useMemo(() => {
        if (isCancelled) return undefined;
        const addon = addons.find((a) => a.name === Feature.PACKAGE_PROVIDER_LIMIT);
        const parsed = parseInt(addon?.value, 10);
        // -1 marks an unlimited addon, treat it as no limit
        return Number.isNaN(parsed) || parsed < 0 ? undefined : parsed;
    }, [addons, isCancelled]);

    const isLoading = isSubscriptionLoading || isSettingsLoading;

    return {
        isLimitExceeded: !isLoading && limit !== undefined && activeCount > limit,
        limit,
        planName: currentSubscription?.name,
        activeCount,
        isLoading,
    };
};

export default useProviderLimitExceeded;
